import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

function extractBodyContent(html) {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match ? match[1] : html;
}

function extractBodyAttributes(html) {
  const match = html.match(/<body([^>]*)>/i);
  const attrs = {};
  if (!match) return attrs;
  const re = /([\w-]+)="([^"]*)"/g;
  let m;
  while ((m = re.exec(match[1])) !== null) attrs[m[1]] = m[2];
  return attrs;
}

function extractTitle(html) {
  const match = html.match(/<title>([^<]*)<\/title>/i);
  return match ? match[1] : "TractionBoard";
}

function extractMetaDescription(html) {
  const match = html.match(
    /<meta[^>]*name="description"[^>]*content="([^"]*)"/i
  );
  return match ? match[1] : null;
}

export default function LandingPage({ pageFile }) {
  const containerRef = useRef(null);
  const [html, setHtml] = useState("");
  const navigate = useNavigate();

  // Fetch HTML page content and set title/meta/body attrs
  useEffect(() => {
    let cancelled = false;
    fetch(`/tb/pages/${pageFile}`)
      .then((r) => r.text())
      .then((content) => {
        if (cancelled) return;
        document.title = extractTitle(content);
        const desc = extractMetaDescription(content);
        if (desc) {
          let el = document.querySelector('meta[name="description"]');
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", "description");
            document.head.appendChild(el);
          }
          el.setAttribute("content", desc);
        }
        // Clear previously set body attrs from other landing pages
        ["data-i18n-title", "data-tb-page"].forEach((a) =>
          document.body.removeAttribute(a)
        );
        const bodyAttrs = extractBodyAttributes(content);
        Object.keys(bodyAttrs).forEach((k) =>
          document.body.setAttribute(k, bodyAttrs[k])
        );
        setHtml(extractBodyContent(content));
      });
    // Scroll to top on navigation
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [pageFile]);

  // After HTML is injected: (re)load i18n, wire link interception, form
  useEffect(() => {
    if (!html) return;
    const container = containerRef.current;
    if (!container) return;

    // Remove any previous dynamic i18n script so init re-runs
    const prev = document.querySelector("script[data-tb-i18n]");
    if (prev) prev.remove();
    const script = document.createElement("script");
    script.src = "/tb/i18n.js?v=17";
    script.setAttribute("data-tb-i18n", "true");
    script.onload = () => {
      if (window.TB_i18n) {
        try {
          window.TB_i18n.init();
        } catch (e) {
          /* noop */
        }
      }
    };
    document.body.appendChild(script);

    // Intercept internal link clicks for SPA navigation
    const linkHandler = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href) return;
      if (
        href.startsWith("http://") ||
        href.startsWith("https://") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }
      if (href.startsWith("#")) {
        e.preventDefault();
        const id = href.slice(1);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        return;
      }
      if (href.startsWith("/")) {
        e.preventDefault();
        // Close any open user-popover so it doesn't linger across SPA nav
        const openPop = a.closest("details[open]");
        if (openPop) openPop.removeAttribute("open");
        if (href.includes("#")) {
          const [pathname, hash] = href.split("#");
          const target = pathname || "/";
          navigate(target);
          setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }, 120);
        } else {
          navigate(href);
        }
      }
    };
    container.addEventListener("click", linkHandler);

    // Lead form (home page only)
    const form = container.querySelector("#lead-form");
    let formHandler = null;
    if (form) {
      formHandler = (e) => {
        e.preventDefault();
        const data = {
          stage: (container.querySelector("#stage") || {}).value || "",
          problem: (container.querySelector("#problem") || {}).value || "",
          traction: (container.querySelector("#traction") || {}).value || "",
          savedAt: new Date().toISOString(),
        };
        try {
          localStorage.setItem(
            "tractionboard_lead_draft",
            JSON.stringify(data)
          );
        } catch (err) {
          /* noop */
        }
        window.location.href = "https://calendar.app.google/FAuG7WsSSo4T2PpTA";
      };
      form.addEventListener("submit", formHandler);
    }

    // Print buttons on gift pages use onclick="window.print()" — those still work

    return () => {
      container.removeEventListener("click", linkHandler);
      if (form && formHandler) form.removeEventListener("submit", formHandler);
    };
  }, [html, navigate]);

  return (
    <div
      ref={containerRef}
      data-testid="landing-page-container"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
